import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { 
  Play, TrendingUp, Settings, LogOut, Menu, X, Shield,
  Activity, ChevronDown
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Two primary destinations only ─────────────────────────────────────────
const mainNavItems = [
  { to: "/protocol", icon: Play, label: "Protocol" },
  { to: "/progress", icon: Activity, label: "Progress" },
];

// Nav Item Component
const NavItem = ({ item, onClick }) => {
  const baseClasses = "nav-item flex items-center gap-3 h-10 pl-4 pr-3 transition-all duration-150 rounded-sm";

  return (
    <NavLink
      to={item.to}
      end={false}
      onClick={onClick}
      className={({ isActive }) =>
        baseClasses + (isActive
          ? " bg-[rgba(200,134,10,0.06)] text-white border-l-2 border-[#C8860A]"
          : " text-[#9BA8BE] hover:text-white hover:bg-[#232A3B] border-l-2 border-transparent")
      }
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      style={{ fontFamily: 'DM Sans, sans-serif' }}
    >
      <item.icon className="w-4 h-4" strokeWidth={1.5} />
      <span className="text-[13px] font-light">{item.label}</span>
    </NavLink>
  );
};

// Zone Label Component
const ZoneLabel = ({ label }) => (
  <div 
    className="px-4 pt-4 pb-2"
    style={{
      fontFamily: 'DM Mono, monospace',
      fontSize: '9px',
      letterSpacing: '0.2em',
      color: '#9BA8BE',
      textTransform: 'uppercase',
    }}
  >
    {label}
  </div>
);

// Zone Divider
const ZoneDivider = () => (
  <div className="my-3 mx-4 h-px bg-[rgba(255,255,255,0.06)]" />
);

export const Layout = ({ children, user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  
  const isAdmin = user?.email === "debumishra@gmail.com";

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    navigate("/");
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-[#0D0F14] flex">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 flex-col fixed left-0 top-0 bottom-0 bg-[#1C2130] border-r border-[rgba(255,255,255,0.07)]">
        {/* Header - Lotus Mark links to dashboard */}
        <div className="p-6 border-b border-[rgba(255,255,255,0.07)] flex items-center gap-3">
          <button onClick={() => navigate("/progress")} className="hover:opacity-80 transition-opacity">
            <img src="/images/clarityos-logo.png" alt="ClarityOS" style={{ height: 32, width: 'auto' }} />
          </button>
        </div>

        {/* Navigation - Protocol · Progress */}
        <nav className="flex-1 overflow-y-auto py-4">
          <div className="space-y-0.5 px-2">
            {mainNavItems.map((item) => (
              <NavItem key={item.to} item={item} />
            ))}
          </div>
        </nav>

        {/* User section with dropdown */}
        <div className="p-4 border-t border-[rgba(255,255,255,0.07)]" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="w-full flex items-center gap-3 px-2 py-2 rounded hover:bg-[#232A3B] transition-colors"
            data-testid="user-menu-btn"
          >
            <Avatar className="w-9 h-9">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-[#232A3B] text-slate-400 text-sm">
                {user?.name?.charAt(0) || "?"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-[13px] font-light text-white truncate">{user?.name}</p>
              <p className="text-[11px] text-slate-500 truncate">{user?.email}</p>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {/* User dropdown menu */}
          {userMenuOpen && (
            <div className="mt-2 py-1 rounded-lg bg-[#232A3B] border border-[rgba(255,255,255,0.07)]">
              <NavLink
                to="/settings"
                onClick={() => setUserMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-slate-400 hover:text-white hover:bg-[#2A3344] transition-colors"
                data-testid="user-menu-settings"
              >
                <Settings className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-[13px] font-light">Settings</span>
              </NavLink>
              {isAdmin && (
                <NavLink
                  to="/admin"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-slate-400 hover:text-white hover:bg-[#2A3344] transition-colors"
                  data-testid="user-menu-admin"
                >
                  <Shield className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-[13px] font-light">Admin</span>
                </NavLink>
              )}
              <div className="my-1 mx-3 h-px bg-[rgba(255,255,255,0.06)]" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-slate-400 hover:text-[#D4723A] hover:bg-[rgba(212,114,58,0.04)] transition-colors"
                data-testid="sidebar-logout"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-[13px] font-light">Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-[#1C2130]/95 backdrop-blur-lg border-b border-[rgba(255,255,255,0.07)]">
        <div className="flex items-center justify-between p-4">
          <button onClick={() => navigate("/progress")} className="hover:opacity-80 transition-opacity">
            <img src="/images/clarityos-logo.png" alt="ClarityOS" style={{ height: 28, width: 'auto' }} />
          </button>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded bg-[#232A3B] hover:bg-[#2A3344] transition-colors"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className="w-5 h-5 text-white" strokeWidth={1.5} />
            ) : (
              <Menu className="w-5 h-5 text-white" strokeWidth={1.5} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#1C2130] border-b border-[rgba(255,255,255,0.07)] max-h-[70vh] overflow-y-auto">
            {/* Main Navigation */}
            <div className="px-2 py-2 space-y-0.5">
              {mainNavItems.map((item) => (
                <NavItem key={item.to} item={item} onClick={closeMobileMenu} />
              ))}
            </div>

            <ZoneDivider />

            {/* Settings & Admin in mobile menu */}
            <div className="px-2 space-y-0.5 pb-4">
              <NavItem item={{ to: "/settings", icon: Settings, label: "Settings" }} onClick={closeMobileMenu} />
              {isAdmin && (
                <NavItem item={{ to: "/admin", icon: Shield, label: "Admin" }} onClick={closeMobileMenu} />
              )}
              <button
                onClick={() => {
                  closeMobileMenu();
                  handleLogout();
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-slate-400 hover:text-[#D4723A] hover:bg-[rgba(212,114,58,0.04)] transition-colors"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-[13px] font-light">Sign Out</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main content */}
      <main className="flex-1 md:ml-64">
        <div className="p-6 md:p-8 pt-20 md:pt-8 max-w-5xl">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Nav - ClarityOS v2 */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#1C2130] border-t border-[rgba(255,255,255,0.07)]">
        <div className="flex items-center justify-around py-2">
          {mainNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={false}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-3 py-2 ${
                  isActive ? "text-[#C8860A]" : "text-slate-500"
                }`
              }
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-[9px]">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};
